// /app/revision/:id — a Revision session (BRIEFS C8.1, SPEC §5 item 7, design
// _v2/design/OneLiner.dc.html). Full-screen layout, no menu.
//
// Top strip: "Revision", the chapter names, "N done · X right · Y wrong", Stop.
// One item at a time from the server (POST /revision/:id/next):
// - a True/False line: True / False → right: green panel "Well done! You are right." with
//   confetti (and the correct line when the line was false); wrong: red panel "Not right."
//   + the correct line;
// - a question: card + options as in the test runner (C7.4 parts); after choosing, the
//   right/wrong labels, "This question: seen N times · correct X · wrong Y" and the answer
//   panel (C11.2: correct answer, explanation, every notes reference as a button).
// After every line, right or wrong (C11.10): "Related notes: 5.4.1 <unit title> ↗" (the
// Notes-with-Back view at /app/revision/:id/notes, bar "Back to Revision"); after either: "Watch video · N min"
// (the C6.1 player, right here) and Next. The answered item is kept in sessionStorage so coming back from the
// notes (or a reload) shows it again instead of a new one.
// Stop → summary (questions and lines: done / right / wrong, minutes) + Start again.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  answerRevision, getRevision, nextRevisionItem, stopRevision,
} from '../../api/v2';
import { track, useActivityArea } from '../../components/app/activity';
import { IconArrowRight, IconBook, IconRight, IconWrong } from '../../components/app/icons';
import {
  AnswerPanel, LETTERS, OptionRow, POINT_CSS, PyqBadge, QId, QuestionBody, errorText, plural, revealedState, unitLine,
} from '../../components/app/testParts';
import VideoModal, { clipMinutes } from '../../components/app/VideoModal';
import { C, FONT, button } from '../../components/app/theme';
import { Loading } from '../../components/app/LoadState';

export const revLastKey = (id) => `v2_rev_last_${id}`;

function readLast(id) {
  try {
    const raw = sessionStorage.getItem(revLastKey(id));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writeLast(id, v) {
  try {
    if (v) sessionStorage.setItem(revLastKey(id), JSON.stringify(v));
    else sessionStorage.removeItem(revLastKey(id));
  } catch { /* ignore */ }
}

// Where "See this in notes" goes: the Notes-with-Back view for Revision.
export function revisionNotesHref(id, target, kind) {
  if (!target || !target.chapter_id) return null;
  const at = target.point_code || target.section_code;
  const qs = new URLSearchParams();
  if (at) qs.set('at', at);
  qs.set('ch', String(target.chapter_id));
  if (kind) qs.set('k', kind === 'question' ? 'q' : 'l');
  return `/app/revision/${id}/notes?${qs.toString()}`;
}

// One "Notes reference" button of a revision question (C11.2) → the same view, at that place.
export function revisionRefHref(id, ref) {
  if (!ref || !ref.chapter_id) return null;
  const qs = new URLSearchParams();
  qs.set('at', ref.point_code || ref.code);
  qs.set('ch', String(ref.chapter_id));
  qs.set('k', 'q');
  return `/app/revision/${id}/notes?${qs.toString()}`;
}

const CONFETTI_CSS = `
@keyframes v2-confetti-fall { 0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
  100% { transform: translateY(120px) rotate(320deg); opacity: 0; } }
.v2-confetti { position: absolute; top: 0; width: 10px; height: 10px; border-radius: 2px;
  animation: v2-confetti-fall 1.5s ease-out forwards; pointer-events: none; }
`;
const CONFETTI = [
  [6, '#E08A2C', 0], [14, '#1D4ED8', 0.1], [22, '#4F46E5', 0.05], [31, '#047857', 0.2],
  [40, '#E08A2C', 0.15], [48, '#1D4ED8', 0.3], [57, '#4F46E5', 0.1], [66, '#E08A2C', 0.25],
  [74, '#047857', 0.05], [82, '#1D4ED8', 0.2], [90, '#4F46E5', 0.35], [95, '#E08A2C', 0.12],
];

function Confetti() {
  const [on, setOn] = useState(true);
  useEffect(() => { const t = setTimeout(() => setOn(false), 1600); return () => clearTimeout(t); }, []);
  if (!on) return null;
  return (
    <div aria-hidden="true" data-testid="v2-confetti">
      {CONFETTI.map(([left, color, delay], i) => (
        <span key={i} className="v2-confetti" style={{
          left: `${left}%`, background: color, animationDelay: `${delay}s`,
          borderRadius: i % 2 ? 5 : 2,
        }} />
      ))}
    </div>
  );
}

const lineText = { fontFamily: FONT.notes, fontSize: 18, lineHeight: 1.5 };

function OneLinerCard({ item, res, busy, onAnswer }) {
  const statement = item.one_liner.statement;
  return (
    <>
      <div style={{ fontSize: 14, fontWeight: 500, color: C.muted }}>True or False?</div>
      <div data-testid="v2-ol-statement" style={{ fontFamily: FONT.notes, fontSize: 28, lineHeight: 1.4 }}>{statement}</div>
      {!res && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {[['True', true], ['False', false]].map(([label, val]) => (
            <button key={label} type="button" disabled={busy} data-testid={`v2-ol-${label.toLowerCase()}`}
              onClick={() => onAnswer(val)} style={{
                flex: '1 1 200px', height: 64, border: `2px solid ${C.primary}`, borderRadius: 12,
                background: C.surface, color: C.primaryDark, fontFamily: FONT.ui, fontSize: 20,
                fontWeight: 600, cursor: busy ? 'default' : 'pointer',
              }}>{label}</button>
          ))}
        </div>
      )}
      {res && res.correct && (
        <div data-testid="v2-ol-right" style={{
          position: 'relative', overflow: 'hidden', boxSizing: 'border-box', padding: 20,
          border: `2px solid ${C.good}`, borderRadius: 12, background: C.goodBg,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <Confetti />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 20, fontWeight: 600, color: '#065F46' }}>
            <IconRight size={24} />Well done! You are right.
          </div>
          {!res.is_true && res.correct_statement && (
            <div data-testid="v2-ol-correct-line" style={lineText}>The line is false. Correct line: {res.correct_statement}</div>
          )}
        </div>
      )}
      {res && !res.correct && (
        <div data-testid="v2-ol-wrong" style={{
          boxSizing: 'border-box', padding: 20, border: `2px solid ${C.bad}`, borderRadius: 12,
          background: C.badBg, display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 20, fontWeight: 600, color: '#991B1B' }}>
            <IconWrong size={24} />
            {res.is_true ? 'Not right. The line is true.' : 'Not right. The line is false.'}
          </div>
          <div data-testid="v2-ol-correct-line" style={lineText}>
            {res.is_true ? `Correct line: ${statement}` : `Correct line: ${res.correct_statement || ''}`}
          </div>
        </div>
      )}
    </>
  );
}

function QuestionCard({ id, item, res, busy, onAnswer }) {
  const q = item.question;
  const body = { passage_text: q.passage, question_text: q.text, question_image_url: (q.images || {}).question };
  const state = (L) => (res ? revealedState(L, res.your_answer, res.correct_option) : 'plain');
  const stat = res && res.stat;
  return (
    <>
      <style>{POINT_CSS}</style>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: C.muted }}>Question</div>
        {res && (
          <span data-testid="v2-rq-status" style={{
            fontSize: 13, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
            background: res.correct ? C.goodBg : C.badBg, color: res.correct ? '#065F46' : '#991B1B',
          }}>{res.correct ? 'Right' : 'Wrong'}</span>
        )}
        <PyqBadge pyq={q.pyq} />
        <QId id={q.id} style={{ marginLeft: 'auto' }} />
      </div>
      <QuestionBody q={body} size={18} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {LETTERS.map((L) => (
          <OptionRow key={L} letter={L} html={(q.options || {})[L]} imageUrl={(q.images || {})[L]}
            state={state(L)} disabled={!!res || busy} onClick={() => onAnswer(L)} />
        ))}
      </div>
      {busy && !res && <div style={{ fontSize: 14, color: C.muted }}>Checking your answer…</div>}
      {res && (
        <>
          {stat && (
            <div data-testid="v2-q-stat" style={{ fontSize: 13, color: '#64748B' }}>
              This question: seen {plural(stat.seen, 'time', 'times')} · correct {stat.correct} · wrong {stat.incorrect}
            </div>
          )}
          <AnswerPanel correctOption={res.correct_option} explanation={res.explanation}
            imageUrl={res.explanation_image_url} refs={res.notes_refs} size={16}
            pointHtml={res.target && res.target.point_html}
            hrefFor={(ref) => revisionRefHref(id, ref)} />
        </>
      )}
    </>
  );
}

function HelpRow({ id, item, res, onVideo }) {
  const target = res && res.target;
  // a question lists its notes places in its answer panel (C11.2); a line keeps this button
  const href = item.kind === 'question' ? null : revisionNotesHref(id, target, item.kind);
  const clips = (res && res.clips) || [];
  const place = unitLine(target);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {href && (
          <Link to={href} state={{ v2back: true }} data-testid="v2-rev-notes" style={{
            display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '0 16px',
            boxSizing: 'border-box', borderRadius: 8, border: `1px solid ${C.primary}`, background: C.surface,
            color: C.primaryDark, fontSize: 14, fontWeight: 600, textDecoration: 'none',
          }}><IconBook stroke={C.primaryDark} />{`${place ? `Related notes: ${place}` : 'Related notes'} \u2197`}</Link>
        )}
        {clips.map((clip) => (
          <button key={clip.id} type="button" data-testid="v2-rev-video" onClick={() => onVideo(clip)} style={{
            display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '0 16px',
            border: 0, borderRadius: 22, background: C.primary, color: '#FFFFFF', fontFamily: FONT.ui,
            fontSize: 14, fontWeight: 500, cursor: 'pointer',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4l13 8-13 8z" /></svg>
            Watch video · {clipMinutes(clip)} min
          </button>
        ))}
      </div>
      {(href || clips.length > 0) && (
        <div style={{ fontSize: 13, color: C.muted }}>Did not understand? Read the notes or watch the video.</div>
      )}
    </div>
  );
}

function Summary({ s }) {
  const row = (label, n, testId) => (
    <div data-testid={testId} style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 16 }}>
      <div style={{ minWidth: 120, fontWeight: 600 }}>{label}</div>
      <div>{n.done} done</div>
      <div style={{ color: C.good }}>{n.right} right</div>
      <div style={{ color: C.bad }}>{n.wrong} wrong</div>
    </div>
  );
  return (
    <div data-testid="v2-rev-summary" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ fontFamily: FONT.heading, fontSize: 24, fontWeight: 600 }}>Revision done</div>
      {row('Lines', s.one_liners, 'v2-rev-sum-lines')}
      {row('Questions', s.questions, 'v2-rev-sum-questions')}
      <div data-testid="v2-rev-sum-minutes" style={{ fontSize: 16 }}>Time: {plural(s.minutes, 'minute', 'minutes')}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <Link to="/app/revision" data-testid="v2-rev-again" style={{
          ...button.primary, display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', boxSizing: 'border-box',
        }}>Start again<IconArrowRight /></Link>
        <Link to="/app" style={{ ...button.secondary, display: 'inline-flex', alignItems: 'center', textDecoration: 'none', boxSizing: 'border-box' }}>Home</Link>
      </div>
    </div>
  );
}

export default function AppRevisionSession() {
  useActivityArea('revision');
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [item, setItem] = useState(null);
  const [res, setRes] = useState(null);
  const [counts, setCounts] = useState({ done: 0, right: 0, wrong: 0 });
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [video, setVideo] = useState(null);
  const shownAt = useRef(Date.now());
  const showSent = useRef(new Set());

  const show = useCallback((it) => {
    setItem(it);
    setRes(null);
    shownAt.current = Date.now();
    if (it && it.kind !== 'none' && it.item && !showSent.current.has(it.item.seq)) {
      showSent.current.add(it.item.seq);
      if (it.kind === 'one_liner') {
        track('ol_show', { test_id: id, one_liner_id: it.one_liner.id, details: { seq: it.item.seq, mode: 'revision' } });
      } else {
        track('question_show', { test_id: id, question_id: it.question.id, details: { seq: it.item.seq, mode: 'v2_revision' } });
      }
    }
  }, [id]);

  const loadNext = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const it = await nextRevisionItem(id);
      writeLast(id, null);
      if (it.counts) setCounts(it.counts);
      show(it);
    } catch (err) {
      const st = err && err.response && err.response.status;
      if (st === 409) {
        const s = await getRevision(id).catch(() => null);
        if (s && s.summary) setSummary(s.summary);
      } else setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }, [id, show]);

  // first load: the session; then the answered item kept from before (back from the notes,
  // reload) or the next item
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await getRevision(id);
        if (!alive) return;
        setSession(s);
        setCounts(s.counts);
        if (s.status === 'completed') { setSummary(s.summary); return; }
        const last = readLast(id);
        if (last && last.item && last.res) {
          setItem(last.item);
          setRes(last.res);
          return;
        }
        loadNext();
      } catch (err) {
        if (alive) setError(errorText(err, 'This revision was not found.'));
      }
    })();
    return () => { alive = false; };
  }, [id]);

  async function answer(value) {
    if (!item || res || busy) return;
    setBusy(true);
    setError('');
    const ms = Date.now() - shownAt.current;
    try {
      let out;
      if (item.kind === 'one_liner') {
        out = await answerRevision(id, {
          kind: 'one_liner', one_liner_id: item.one_liner.id, answered_true: value, time_ms: ms,
        });
        track('ol_answer', {
          test_id: id, one_liner_id: item.one_liner.id, duration_ms: ms,
          details: { answered_true: value, correct: out.correct, seq: item.item.seq },
        });
      } else {
        out = await answerRevision(id, {
          kind: 'question', question_id: item.question.id, answer: value,
          time_spent_seconds: Math.round(ms / 1000),
        });
        track('answer', {
          test_id: id, question_id: item.question.id, duration_ms: ms,
          details: { answer: value, correct: out.correct, mode: 'v2_revision', seq: item.item.seq },
        });
      }
      setRes(out);
      if (out.counts) setCounts(out.counts);
      writeLast(id, { item, res: out });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function stop() {
    if (busy) return;
    setBusy(true);
    try {
      const out = await stopRevision(id);
      track('rev_stop', { test_id: id, details: { ...out } });
      writeLast(id, null);
      setSummary(out);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const chapters = (session && session.chapters) || [];
  const chapterLine = chapters.length === 1 ? chapters[0].title
    : chapters.length ? `${chapters.length} chapters · ${chapters.map((c) => c.title).join(', ')}` : '';

  return (
    <div data-testid="v2-revision" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: C.bg }}>
      <style>{CONFETTI_CSS}</style>
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, padding: '10px 24px',
        background: C.surface, borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Revision</div>
          <div data-testid="v2-rev-chapters" style={{ fontSize: 13, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chapterLine}</div>
        </div>
        <div data-testid="v2-rev-counts" style={{ flex: '1 1 220px', fontSize: 14, color: C.muted }}>
          {counts.done} done · {counts.right} right · {counts.wrong} wrong
        </div>
        {!summary && (
          <button type="button" data-testid="v2-rev-stop" onClick={stop} disabled={busy} style={{
            height: 44, padding: '0 18px', border: '1px solid #CBD5E1', borderRadius: 8,
            background: C.surface, color: C.text, fontFamily: FONT.ui, fontSize: 14, cursor: 'pointer',
          }}>Stop</button>
        )}
      </div>

      <main style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', boxSizing: 'border-box', padding: '40px 24px' }}>
        <div data-testid="v2-rev-card" data-kind={item ? item.kind : ''} style={{
          width: '100%', maxWidth: 720, boxSizing: 'border-box', padding: 36, background: C.surface,
          border: `1px solid ${C.border}`, borderRadius: 16, display: 'flex', flexDirection: 'column', gap: 22,
        }}>
          {error && <div data-testid="v2-rev-error" style={{ color: C.bad, fontSize: 15 }}>{error}</div>}
          {summary ? <Summary s={summary} /> : (
            <>
              {!item && !error && <Loading />}
              {item && item.kind === 'none' && (
                <div data-testid="v2-rev-none" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ fontFamily: FONT.heading, fontSize: 20, fontWeight: 600 }}>You have done everything in these chapters for now.</div>
                  <div style={{ fontSize: 15, color: C.muted }}>Press Stop to see how you did, or choose more chapters.</div>
                  <div><button type="button" onClick={stop} style={button.primary}>Stop and see my result</button></div>
                </div>
              )}
              {item && item.kind === 'one_liner' && <OneLinerCard item={item} res={res} busy={busy} onAnswer={answer} />}
              {item && item.kind === 'question' && <QuestionCard id={id} item={item} res={res} busy={busy} onAnswer={answer} />}
              {item && res && (
                <>
                  <HelpRow id={id} item={item} res={res} onVideo={setVideo} />
                  <button type="button" data-testid="v2-rev-next" onClick={loadNext} disabled={busy} style={{
                    alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: 8, height: 48,
                    padding: '0 22px', border: 0, borderRadius: 10, background: C.primary, color: '#FFFFFF',
                    fontFamily: FONT.ui, fontSize: 15, fontWeight: 600, cursor: 'pointer',
                  }}>Next<IconArrowRight /></button>
                </>
              )}
            </>
          )}
        </div>
        {!summary && !item && error && (
          <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button type="button" data-testid="v2-try-again"
              onClick={() => (session ? loadNext() : window.location.reload())} style={button.primary}>Try again</button>
            <button type="button" onClick={() => navigate('/app/revision')} style={button.secondary}>Back to Revision</button>
          </div>
        )}
      </main>
      {video && (
        <VideoModal clip={video} chapterId={res && res.target ? res.target.chapter_id : undefined}
          unitCode={video.section_code} unitLabel={unitLine(res && res.target)} onClose={() => setVideo(null)} />
      )}
    </div>
  );
}
